/**
 * Deutsche Beschriftungen für jeden Aufzählungswert — die **einzige** Quelle dafür,
 * genutzt von Server und Frontend.
 *
 * Der entscheidende Punkt sind die Typen: jede Tabelle ist ein `Record<Category, string>`
 * und **nicht** `Record<string, string>`. Wer einen Wert zu `CATEGORIES` hinzufügt und
 * die Beschriftung vergisst, bekommt keinen englischen Rohwert im UI, sondern einen
 * Compilerfehler. Vorher lagen drei Kopien dieser Tabellen im Projekt (zwei im
 * Frontend, eine im Server), die bereits auseinandergelaufen waren.
 *
 * Warum das nicht nur Kosmetik ist: Der Nutzer soll der App vertrauen können. Wer
 * „Zustand at_care" liest, sieht ein Programm, das durchscheint — und zweifelt zu Recht,
 * ob dahinter jemand nachgedacht hat.
 */
export const CATEGORY_LABELS = {
    shirt: 'Hemd',
    tshirt: 'T-Shirt',
    polo: 'Poloshirt',
    knitwear: 'Strick',
    jacket: 'Jacke',
    coat: 'Mantel',
    suit_jacket: 'Sakko',
    trousers: 'Hose',
    jeans: 'Jeans',
    shorts: 'Shorts',
    shoes: 'Schuhe',
    boots: 'Stiefel',
    sneakers: 'Sneaker',
    belt: 'Gürtel',
    tie: 'Krawatte',
    scarf: 'Schal',
    gloves: 'Handschuhe',
    hat: 'Mütze',
    socks: 'Socken',
    underwear: 'Unterwäsche',
    other: 'Sonstiges',
};
/** `base` heißt im Slot-Vokabular `top` (D-05) — beide führen zu „Oberteil". */
export const LAYER_LABELS = {
    base: 'Oberteil',
    mid: 'Zwischenschicht',
    outer: 'Außenschicht',
    bottom: 'Hose',
    footwear: 'Schuhe',
    belt: 'Gürtel',
    accessory: 'Accessoire',
};
export const SLOT_LABELS = {
    top: 'Oberteil',
    mid: 'Zwischenschicht',
    outer: 'Außenschicht',
    bottom: 'Hose',
    footwear: 'Schuhe',
    belt: 'Gürtel',
    accessory: 'Accessoire',
};
export const LIFECYCLE_STATE_LABELS = {
    clean: 'sauber',
    worn_ok: 'getragen',
    soiled: 'zu waschen',
    in_hamper: 'im Wäschekorb',
    washing: 'in der Wäsche',
    drying: 'trocknet',
    ironing: 'zu bügeln',
    at_care: 'in der Reinigung',
    retired: 'ausgemustert',
};
export const LOCATION_LABELS = {
    wardrobe: 'im Schrank',
    hamper: 'im Wäschekorb',
    laundry: 'in der Waschmaschine',
    cleaner: 'in der Reinigung',
    tailor: 'beim Schneider',
    trip: 'auf Reisen',
};
export const CARE_POLICY_LABELS = {
    machine_wash: 'Maschinenwäsche',
    hand_wash: 'Handwäsche',
    dry_clean: 'nur Reinigung',
    air_only: 'nur auslüften',
};
export const LAUNDRY_GROUP_LABELS = {
    dark: 'Dunkles',
    light: 'Helles',
    white: 'Weißes',
    color: 'Buntes',
    delicate: 'Feinwäsche',
    wool: 'Wolle',
};
export const OCCASION_LABELS = {
    home_office: 'Homeoffice',
    office: 'Büro',
    client_meeting: 'Kundentermin',
    evening: 'Abends',
    travel: 'Reise',
    sport: 'Sport',
    other: 'Sonstiges',
};
/** Für den Verlauf eines Teils: was ist passiert, in der Vergangenheitsform. */
export const ITEM_ACTION_LABELS = {
    wear: 'getragen',
    to_hamper: 'in den Wäschekorb gelegt',
    start_wash: 'Waschgang gestartet',
    finish_wash: 'Waschgang beendet',
    drying_done: 'getrocknet',
    ironed: 'gebügelt',
    send_to_care: 'in die Reinigung gegeben',
    care_returned: 'aus der Reinigung zurück',
    retire: 'ausgemustert',
    unretire: 'wieder in Benutzung',
    manual_edit: 'von Hand geändert',
    learning: 'Lernschritt',
    trip_reserve: 'für eine Reise reserviert',
    trip_release: 'von der Reise freigegeben',
};
export const CARE_TYPE_LABELS = {
    dry_clean: 'Reinigung',
    tailor: 'Änderung',
    repair: 'Reparatur',
    impregnate: 'Imprägnieren',
};
export const TRIP_STATUS_LABELS = {
    planned: 'geplant',
    active: 'läuft',
    done: 'erledigt',
    cancelled: 'abgesagt',
};
export const WEATHER_SOURCE_LABELS = {
    forecast: 'Prognose',
    actual: 'Nachlauf',
};
/**
 * Die Felder, die die Bildanalyse vorschlägt. Sie stehen im Erfassungsbildschirm im
 * Klartext („unsicher war die Erkennung bei …") — englische Schlüssel wie `warmthPoints`
 * gehören dort nicht hin. Die Liste ist in `gemini.ts` gegen den Vorschlagstyp geprüft,
 * damit ein neues Feld nicht unbeschriftet durchrutscht.
 */
export const ANALYSIS_FIELDS = [
    'name', 'brand', 'category', 'subcategory', 'layer', 'colorPrimaryName', 'colorPrimaryHex',
    'colorSecondaryName', 'pattern', 'material', 'formality', 'warmthPoints',
    'carePolicy', 'laundryGroup', 'washTempC', 'needsIroning',
];
export const ANALYSIS_FIELD_LABELS = {
    name: 'Name',
    brand: 'Marke',
    category: 'Kategorie',
    subcategory: 'Unterkategorie',
    layer: 'Schicht',
    colorPrimaryName: 'Farbe',
    colorPrimaryHex: 'Farbton',
    colorSecondaryName: 'Zweitfarbe',
    pattern: 'Muster',
    material: 'Material',
    formality: 'Formalität',
    warmthPoints: 'Wärme',
    carePolicy: 'Pflege',
    laundryGroup: 'Wäschegruppe',
    washTempC: 'Waschtemperatur',
    needsIroning: 'Bügeln',
};
/**
 * Die Felder, die die App selbst lernt (§7). `learning_events.field` ist freier Text —
 * diese Liste ist der Katalog dazu, und die typisierte Tabelle darunter erzwingt für
 * jedes neue Lernziel eine deutsche Bezeichnung. Im Verlauf eines Teils stand bisher
 * wörtlich „temp_offset_c: 0.00 → 0.30" — ein Spaltenname und zwei Zahlen ohne Einheit.
 */
export const LEARNED_FIELDS = [
    'temp_offset_c', 'user_thermal_bias',
    'wears_per_wash', 'drying_hours', 'wears_per_clean',
];
/**
 * Die Pflegewerte aus D-38 — zugleich die Spaltennamen in `items`. Anders als
 * `temp_offset_c`, das als eigene Korrekturspalte neben dem Startwert steht (D-01),
 * überschreibt die Pflege-Schleife den Wert selbst. Der Weg zurück führt deshalb über
 * `learning_events`: der älteste nicht zurückgenommene Eintrag trägt den ursprünglichen
 * Wert.
 */
export const CARE_LEARNED_FIELDS = [
    'wears_per_wash', 'drying_hours', 'wears_per_clean',
];
export const LEARNED_FIELD_LABELS = {
    temp_offset_c: 'Wärme-Korrektur',
    user_thermal_bias: 'Kälteempfinden insgesamt',
    wears_per_wash: 'Tragungen bis zur Wäsche',
    drying_hours: 'Trockenzeit',
    wears_per_clean: 'Tragungen bis zur Reinigung',
};
/** Einheit je Lernfeld und die Zahl der sinnvollen Nachkommastellen. */
const LEARNED_FIELD_FORMAT = {
    temp_offset_c: { einheit: ' K', stellen: 2 },
    user_thermal_bias: { einheit: ' K', stellen: 2 },
    wears_per_wash: { einheit: '×', stellen: 0 },
    drying_hours: { einheit: ' h', stellen: 1 },
    wears_per_clean: { einheit: '×', stellen: 0 },
};
/**
 * Ein gelernter Wert mit Einheit. Ohne sie ist „0,30" nicht von „30 %" oder „0,3 Tagen"
 * zu unterscheiden — und der ganze Zweck der Lernanzeige ist Nachvollziehbarkeit.
 */
export function formatLearnedValue(field, value) {
    const format = LEARNED_FIELD_FORMAT[field];
    if (format === undefined)
        return formatNumber(value, 2);
    return `${formatNumber(value, format.stellen)}${format.einheit}`;
}
/**
 * Nachschlagen mit Rückfall auf den Rohwert.
 *
 * Der Rückfall ist ausdrücklich **keine** Absicherung gegen vergessene Beschriftungen —
 * dagegen steht der Typ. Er greift nur, wenn ein Wert aus einer alten Datenbank oder
 * einem Import kommt, den der aktuelle Katalog nicht mehr kennt. Dann ist der Rohwert
 * ehrlicher als eine leere Zelle.
 */
function lookup(table, value) {
    return table[value] ?? value;
}
export const labelOf = {
    category: (v) => lookup(CATEGORY_LABELS, v),
    layer: (v) => lookup(LAYER_LABELS, v),
    slot: (v) => lookup(SLOT_LABELS, v),
    lifecycleState: (v) => lookup(LIFECYCLE_STATE_LABELS, v),
    location: (v) => lookup(LOCATION_LABELS, v),
    carePolicy: (v) => lookup(CARE_POLICY_LABELS, v),
    laundryGroup: (v) => lookup(LAUNDRY_GROUP_LABELS, v),
    occasion: (v) => lookup(OCCASION_LABELS, v),
    itemAction: (v) => lookup(ITEM_ACTION_LABELS, v),
    careType: (v) => lookup(CARE_TYPE_LABELS, v),
    tripStatus: (v) => lookup(TRIP_STATUS_LABELS, v),
    weatherSource: (v) => lookup(WEATHER_SOURCE_LABELS, v),
    analysisField: (v) => lookup(ANALYSIS_FIELD_LABELS, v),
    learnedField: (v) => lookup(LEARNED_FIELD_LABELS, v),
};
/**
 * Deutsches Datum aus einem ISO-Zeitstempel oder `YYYY-MM-DD`.
 * Ein roher Zeitstempel im Fließtext („gesperrt bis 2026-08-15T06:00:00.000Z") ist für
 * niemanden lesbar.
 */
export function formatDate(iso, mitUhrzeit = false) {
    const date = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        ...(mitUhrzeit ? { hour: '2-digit', minute: '2-digit' } : {}),
    });
}
/** Deutsche Zahl: 7,4 statt 7.4. Ganze Zahlen bleiben ohne Nachkommastelle. */
export function formatNumber(value, maxStellen = 1) {
    return value.toLocaleString('de-DE', { maximumFractionDigits: maxStellen });
}
//# sourceMappingURL=labels.js.map